/**
 * Sortable, searchable roster (brief §teacher: `/teacher/students`). Every
 * row links to the student detail page for mastery, recent attempts and role
 * management.
 */
import { useState } from "react";
import { Link } from "react-router";
import { Badge, Input, PageHeader, Select, Spinner, Table, Tabs } from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { formatDate, formatNumber, formatPercent, formatPoints } from "@client/lib/format";
import { TeacherNav } from "./TeacherNav";
import { useTeacherStudents } from "./api";
import type { StudentRoleFilter, StudentSort, StudentSummary } from "./types";

export function TeacherStudentsPage() {
  const t = useT();
  const { lang } = useLang();
  const [sort, setSort] = useState<StudentSort>("lastActive");
  const [q, setQ] = useState("");
  const [role, setRole] = useState<StudentRoleFilter>("student");
  const query = useTeacherStudents(sort, q, role);

  const sortOptions = [
    { value: "lastActive", label: t("teacher.students.sort.lastActive") },
    { value: "attempts", label: t("teacher.students.sort.attempts") },
    { value: "accuracy", label: t("teacher.students.sort.accuracy") },
  ];

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title={t("teacher.students.title")}
        description={t("teacher.students.description")}
      />
      <TeacherNav />

      <Tabs
        ariaLabel={t("teacher.students.roleFilter.label")}
        value={role}
        onChange={(id) => setRole(id as StudentRoleFilter)}
        items={[
          { id: "student", label: t("teacher.students.roleFilter.student") },
          { id: "teacher", label: t("teacher.students.roleFilter.teacher") },
          { id: "all", label: t("teacher.students.roleFilter.all") },
        ]}
        className="mb-4"
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Input
          label={t("teacher.students.search.label")}
          placeholder={t("teacher.students.search.placeholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          containerClassName="min-w-[200px] flex-1"
        />
        <Select
          label={t("teacher.students.sort.label")}
          options={sortOptions}
          value={sort}
          onChange={(e) => setSort(e.target.value as StudentSort)}
        />
      </div>

      {query.isLoading && <Spinner size="md" label={t("common.loading")} />}

      {query.data && (
        <Table<StudentSummary>
          columns={[
            {
              key: "displayName",
              header: t("teacher.students.table.name"),
              cell: (row) => (
                <Link
                  to={`/teacher/students/${row.id}`}
                  className="font-medium text-accent-700 hover:underline dark:text-accent-300"
                >
                  {row.displayName}
                </Link>
              ),
            },
            { key: "email", header: t("teacher.students.table.email") },
            {
              key: "role",
              header: t("teacher.students.table.role"),
              cell: (row) => (
                <Badge tone={row.role === "teacher" ? "accent" : "neutral"}>
                  {t(`teacher.role.${row.role}`)}
                </Badge>
              ),
            },
            {
              key: "attempts",
              header: t("teacher.students.table.attempts"),
              align: "right",
              cell: (row) => formatNumber(row.attempts, lang),
            },
            {
              key: "autoAccuracy",
              header: t("teacher.students.table.autoAccuracy"),
              align: "right",
              cell: (row) => formatPercent(row.autoAccuracy, lang),
            },
            {
              key: "selfAccuracy",
              header: t("teacher.students.table.selfAccuracy"),
              align: "right",
              cell: (row) => formatPercent(row.selfAccuracy, lang),
            },
            {
              key: "bestExamScore",
              header: t("teacher.students.table.bestExamScore"),
              align: "right",
              cell: (row) =>
                row.bestExamScore === null ? "—" : formatPoints(row.bestExamScore, lang),
            },
            {
              key: "lastActiveAt",
              header: t("teacher.students.table.lastActive"),
              align: "right",
              cell: (row) => formatDate(row.lastActiveAt, lang),
            },
          ]}
          rows={query.data}
          rowKey={(row) => row.id}
          empty={
            <p className="text-sm text-ink-600 dark:text-ink-400">{t("teacher.students.empty")}</p>
          }
        />
      )}
    </div>
  );
}

export default TeacherStudentsPage;
